import { actorConnectionStats } from './buildGraph';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useRegisterEvents,
  useSetSettings,
  useSigma,
} from '@react-sigma/core';
import type Graph from 'graphology';
import type Sigma from 'sigma';
import type { EdgeDisplayData, NodeDisplayData } from 'sigma/types';
import {
  EdgeAttributes,
  edgeVolumeSize,
  FAR_ZOOM_CAMERA_RATIO,
  FAR_ZOOM_EDGE_LIMIT,
  GraphHighlight,
  matchesHighlight,
  NodeAttributes,
  TooltipState,
} from './graphTypes';
import {
  categoryHighlightColor,
  CONTEXT_EDGE_MUTE,
  CONTEXT_NODE_MUTE,
  DIMMED_NODE_COLOR,
  mixWithBackgroundCached,
  SCOPE_INBOUND_COLOR,
  SCOPE_OUTBOUND_COLOR,
  zscoreEdgeStyle,
} from './graphColors';
import type { TimelineSelection } from './timelineIndex';
import { createDrawNodeLabel } from './drawNodeLabel';
import { FALLBACK_PLATFORM_ICON, PLATFORM_ICONS } from './platformIcons';
import { platformNamesMapping } from '../../utils/platforms';
import styles from './SocialGraphPOC.module.scss';

// How long the cursor must rest on a node before the rest of the graph dims.
// Sweeping across nodes never triggers the dim (each leave cancels the timer),
// so exploration doesn't strobe; a deliberate pause reads as intent.
const HOVER_DIM_DELAY_MS = 300;

// While an actor is selected/dwelled, ITS edges scale thickness by interaction
// volume (investigator story: the strongest ties should be the most obvious).
// The formula is shared with buildGraph's resting sizes (graphTypes.ts), so
// with VOLUME_SIZED_RESTING_EDGES on, selecting a node changes color/arrows
// but never thickness — no jarring resize on click.

// Inputs of the render-time reducers, resolved once per state change (see the
// effects in the component); the reducers themselves never change identity
interface ReducerState {
  activeNode: string | null;
  focusedNode: string | null;
  showKeyConnectors: boolean;
  highlight: GraphHighlight | null;
  zscoreEdgeColoring: boolean;
  arrowType: 'arrow' | 'curvedArrow';
  // far-zoom thinning in effect: `thinned` edges are skipped
  thinning: boolean;
  // timeline selection (null = no time filter)
  activeEdges: Set<string> | null;
  activeNodes: Set<string> | null;
  // the active node's neighbourhood
  neighbors: Set<string> | null;
  outEdges: Set<string> | null;
  inEdges: Set<string> | null;
}

const IDLE_REDUCER_STATE: ReducerState = {
  activeNode: null,
  focusedNode: null,
  showKeyConnectors: false,
  highlight: null,
  zscoreEdgeColoring: false,
  arrowType: 'curvedArrow',
  thinning: false,
  activeEdges: null,
  activeNodes: null,
  neighbors: null,
  outEdges: null,
  inEdges: null,
};

type NodeData = Partial<NodeDisplayData>;
type EdgeData = Partial<EdgeDisplayData>;

function reduceNode(node: string, data: NodeData, s: ReducerState): NodeData {
  const { isKeyConnector } = data as unknown as { isKeyConnector?: boolean };
  const connectorLabel = s.showKeyConnectors && !!isKeyConnector;
  if (!s.activeNode && !s.activeNodes && !s.highlight) {
    const showLabel = connectorLabel || node === s.focusedNode;
    return showLabel ? { ...data, forceLabel: true } : data;
  }
  if (s.activeNodes && !s.activeNodes.has(node)) {
    return { ...data, hidden: true };
  }
  if (s.activeNode) {
    if (node === s.activeNode) {
      return { ...data, zIndex: 1, forceLabel: true, highlighted: false };
    }
    if ((s.neighbors as Set<string>).has(node)) {
      return { ...data, zIndex: 1, forceLabel: connectorLabel || node === s.focusedNode, highlighted: false };
    }
    // non-neighbors stay visible with their hue muted — the surrounding
    // communities are the context the user reads the selection against
    // (fully hiding them shipped once; users lost the graph's structure).
    // label: null also skips the piggybacked connector ring — fine,
    // these nodes used to be hidden entirely
    return {
      ...data,
      color: mixWithBackgroundCached(data.color as string, CONTEXT_NODE_MUTE),
      zIndex: 0,
      label: null,
      forceLabel: false,
    };
  }
  if (s.highlight) {
    // community/role spotlight from the panel: dim non-members toward the
    // background. A facet-dimmed node ("Highlight instead of hide") stays gray
    // context even when it belongs to the spotlighted group
    if (
      !matchesHighlight(data as unknown as NodeAttributes, s.highlight) ||
      (data as unknown as NodeAttributes).filterDimmed
    ) {
      return { ...data, color: DIMMED_NODE_COLOR, zIndex: 0, forceLabel: false };
    }
    // category spotlights repaint members in the red/blue signal hue (the
    // legend mirrors it); community/z-score spotlights keep members' own colors
    const categoryColor = categoryHighlightColor(s.highlight);
    if (categoryColor) {
      return { ...data, color: categoryColor, zIndex: 1, forceLabel: connectorLabel || node === s.focusedNode };
    }
  }
  // time filter or spotlight active, no hover
  return { ...data, zIndex: 1, forceLabel: connectorLabel || node === s.focusedNode };
}

function reduceEdge(
  edge: string,
  data: EdgeData,
  graph: Graph<NodeAttributes, EdgeAttributes>,
  s: ReducerState,
): EdgeData {
  if (s.activeEdges && !s.activeEdges.has(edge)) {
    return { ...data, hidden: true };
  }
  const thinnedOut = s.thinning && (data as unknown as EdgeAttributes).thinned === true;
  if (s.activeNode) {
    const outgoing = (s.outEdges as Set<string>).has(edge);
    if (outgoing || (s.inEdges as Set<string>).has(edge)) {
      // reveal even edges hidden by the cross-cluster mode or thinned at this
      // zoom — the active node's links are exactly what the user is inspecting —
      // but NOT edges the relation-type filter removed on purpose. Thickness =
      // interaction volume and the arrowed program is forced so direction stays
      // readable even when the global "Direction arrows" switch is off.
      // In z-score analysis anomaly outranks direction: anomalous edges keep
      // their stored red/orange anomaly color (designer spec)
      const anomalous = s.zscoreEdgeColoring && zscoreEdgeStyle((data as unknown as EdgeAttributes).zScore).anomalous;
      return {
        ...data,
        // orange = the node mentions someone (outgoing), cyan = someone mentions it
        ...(anomalous ? {} : { color: outgoing ? SCOPE_OUTBOUND_COLOR : SCOPE_INBOUND_COLOR }),
        size: edgeVolumeSize((data as unknown as EdgeAttributes).totalCount),
        type: s.arrowType,
        zIndex: 2,
        hidden: (data as unknown as EdgeAttributes).typeFiltered === true,
      };
    }
    if (thinnedOut) return { ...data, hidden: true };
    // keep a faint trace of the edge's own hue instead of a flat
    // near-background gray — inter-community links stay legible
    return { ...data, color: mixWithBackgroundCached(data.color as string, CONTEXT_EDGE_MUTE), zIndex: 0 };
  }
  if (thinnedOut) return { ...data, hidden: true };
  if (s.highlight) {
    // endpoints resolved here only: the spotlight path reads live node
    // attributes (filterDimmed changes with the facet filters without
    // re-running the effects)
    const [source, target] = graph.extremities(edge);
    const sourceAttrs = graph.getNodeAttributes(source);
    const targetAttrs = graph.getNodeAttributes(target);
    const bothMatch =
      !sourceAttrs.filterDimmed &&
      !targetAttrs.filterDimmed &&
      matchesHighlight(sourceAttrs, s.highlight) &&
      matchesHighlight(targetAttrs, s.highlight);
    return bothMatch
      ? { ...data, zIndex: 1 }
      : { ...data, color: mixWithBackgroundCached(data.color as string, CONTEXT_EDGE_MUTE), zIndex: 0 };
  }
  return data;
}

// Re-reduce every item and reprocess (draw order, label grid) without sigma's
// index rebuild — the equivalent of its own attributes-updated handler
function refreshAll(sigma: Sigma<NodeAttributes, EdgeAttributes>): void {
  const graph = sigma.getGraph();
  sigma.refresh({ partialGraph: { nodes: graph.nodes(), edges: graph.edges() }, skipIndexation: false, schedule: true });
}

function symmetricDifference(a: Set<string>, b: Set<string>): string[] {
  const out: string[] = [];
  a.forEach((key) => {
    if (!b.has(key)) out.push(key);
  });
  b.forEach((key) => {
    if (!a.has(key)) out.push(key);
  });
  return out;
}

export const GraphHoverEffects: React.FC<{
  focusedNode: string | null;
  showKeyConnectors: boolean;
  onFocusNode: (nodeId: string | null) => void;
  // timeline filter (strip window / selected day / hover / playback frame):
  // graphology edge keys + node ids with activity in the period, null = no filter
  timeSelection: TimelineSelection | null;
  brushNodeIds: Set<string> | null;
  highlight: GraphHighlight | null;
  // z-score analysis active: anomaly styling outranks the in/out recolor on the
  // active node's edges
  zscoreEdgeColoring: boolean;
  // straight edge programs are in use (SocialGraphPOC performance mode)
  performanceMode: boolean;
  // far-zoom thinning became active / inactive (for the canvas note)
  onThinningChange?: (active: boolean) => void;
}> = ({
  focusedNode,
  showKeyConnectors,
  onFocusNode,
  timeSelection,
  brushNodeIds,
  highlight,
  zscoreEdgeColoring,
  performanceMode,
  onThinningChange,
}) => {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const registerEvents = useRegisterEvents();
  const setSettings = useSetSettings<NodeAttributes, EdgeAttributes>();
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // hover only engages the graph-wide dim after the cursor DWELLS on a node —
  // instant dimming made the whole graph flicker while users swept across it
  // (user complaint). Tooltip + cursor stay immediate via hoveredNode; only the
  // reducers wait for dwelledNode. Leaving clears both at once.
  const [dwelledNode, setDwelledNode] = useState<string | null>(null);
  const dwellTimerRef = useRef<number | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const activeNodeRef = useRef<string | null>(null);

  const activeNode = dwelledNode ?? focusedNode;

  const updateTooltipPosition = useCallback(() => {
    const n = activeNodeRef.current;
    if (!n) return;
    const display = sigma.getNodeDisplayData(n);
    if (!display) return;
    const { x, y } = sigma.framedGraphToViewport({
      x: display.x,
      y: display.y,
    });
    setTooltip((prev) => (prev ? { ...prev, x, y } : prev));
  }, [sigma]);

  useEffect(() => {
    registerEvents({
      enterNode: (event) => {
        setHoveredNode(event.node);
        if (dwellTimerRef.current !== null)
          window.clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = window.setTimeout(() => {
          setDwelledNode(event.node);
        }, HOVER_DIM_DELAY_MS);
      },
      leaveNode: () => {
        setHoveredNode(null);
        if (dwellTimerRef.current !== null) {
          window.clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        setDwelledNode(null);
      },
      afterRender: updateTooltipPosition,
      clickNode: (event) =>
        onFocusNode(event.node === focusedNode ? null : event.node),
      clickStage: () => onFocusNode(null),
    });
  }, [registerEvents, updateTooltipPosition, focusedNode, onFocusNode]);

  // don't let a pending dwell timer fire after unmount
  useEffect(
    () => () => {
      if (dwellTimerRef.current !== null)
        window.clearTimeout(dwellTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // only consume Escape when a node is actually pinned — otherwise let it
      // bubble to an enclosing overlay's close handler
      if (e.key === 'Escape' && focusedNode !== null) {
        e.preventDefault();
        onFocusNode(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onFocusNode, focusedNode]);

  useEffect(() => {
    sigma.getContainer().style.cursor = hoveredNode ? 'pointer' : '';
  }, [hoveredNode, sigma]);

  useEffect(() => {
    if (!focusedNode) return;
    const graph = sigma.getGraph();
    if (!graph.hasNode(focusedNode)) return;
    // Convert the node's stored (layout) coordinates to framed coordinates
    // ourselves instead of react-sigma's gotoNode, which reads
    // sigma.getNodeDisplayData(). That cache is re-indexed synchronously with
    // RAW coordinates on every sigma.setSetting() call and only re-normalized
    // on the next animation frame — and the reducer effect below calls
    // setSettings on every hover change and every timeline playback tick. A
    // click landing in that window used to fly the camera to a raw layout
    // coordinate (hundreds of framed units away): the graph vanished and only
    // the empty canvas remained until a reset.
    const { x, y } = graph.getNodeAttributes(focusedNode);
    // never animate the camera toward a non-finite coordinate: sigma's
    // validateState only checks `typeof === 'number'`, which NaN passes, so a
    // bad target poisons the camera state (graph shrinks into a corner or the
    // canvas goes blank) until a reset/reload
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      console.warn(`Social graph: node ${focusedNode} has non-finite coordinates — camera move skipped`);
      return;
    }
    const target = sigma.viewportToFramedGraph(sigma.graphToViewport({ x, y }));
    void sigma.getCamera().animate(target, { duration: 600 });
  }, [focusedNode, sigma]);

  useEffect(() => {
    const drawFn = createDrawNodeLabel(showKeyConnectors);
    setSettings({ defaultDrawNodeLabel: drawFn, defaultDrawNodeHover: drawFn });
  }, [showKeyConnectors, setSettings]);

  useEffect(() => {
    if (focusedNode && brushNodeIds && !brushNodeIds.has(focusedNode)) {
      onFocusNode(null);
    }
  }, [focusedNode, brushNodeIds, onFocusNode]);

  // Far-zoom thinning (team decision: strongest-only edges when zoomed out).
  // Edges ranked below FAR_ZOOM_EDGE_LIMIT at build time carry `thinned`; they
  // are skipped while the camera ratio is above FAR_ZOOM_CAMERA_RATIO. Only the
  // band flip reaches React state, so camera animation frames cost nothing.
  const thinnable = sigma.getGraph().size > FAR_ZOOM_EDGE_LIMIT;
  const [farZoom, setFarZoom] = useState(() => sigma.getCamera().ratio > FAR_ZOOM_CAMERA_RATIO);
  useEffect(() => {
    const camera = sigma.getCamera();
    const handler = () => {
      const next = camera.ratio > FAR_ZOOM_CAMERA_RATIO;
      setFarZoom((prev) => (prev === next ? prev : next));
    };
    handler();
    camera.on('updated', handler);
    return () => {
      camera.off('updated', handler);
    };
  }, [sigma]);
  const thinning = thinnable && farZoom;
  useEffect(() => {
    onThinningChange?.(thinning);
  }, [thinning, onThinningChange]);

  // The reducers run once per node and per edge on every reprocess, so they are
  // installed ONCE per Sigma instance and read their inputs from a ref: sigma's
  // setSettings always re-indexes the whole graph, which is what every hover
  // change and playback tick used to pay. Inputs that touch every item (hover,
  // spotlight, zoom band…) trigger one full reprocess; a timeline step refreshes
  // only the items whose membership changed (scaling plan 1.3).
  const reducerStateRef = useRef<ReducerState>(IDLE_REDUCER_STATE);
  useEffect(() => {
    const graph = sigma.getGraph();
    setSettings({
      nodeReducer: (node, data) => reduceNode(node, data, reducerStateRef.current),
      edgeReducer: (edge, data) => reduceEdge(edge, data, graph, reducerStateRef.current),
    });
  }, [sigma, setSettings]);

  // everything except the timeline selection: resolve once, reprocess all
  useEffect(() => {
    const graph = sigma.getGraph();
    reducerStateRef.current = {
      ...reducerStateRef.current,
      activeNode,
      focusedNode,
      showKeyConnectors,
      highlight,
      zscoreEdgeColoring,
      thinning,
      // the active node's edges always show direction, in the straight program
      // when performance mode has swapped the curves out
      arrowType: performanceMode ? 'arrow' : 'curvedArrow',
      neighbors: activeNode ? new Set(graph.neighbors(activeNode)) : null,
      // the active node's own edges by direction, so the edge reducer can tell
      // outgoing from incoming without resolving endpoints
      outEdges: activeNode ? new Set(graph.outEdges(activeNode)) : null,
      inEdges: activeNode ? new Set(graph.inEdges(activeNode)) : null,
    };
    refreshAll(sigma);
  }, [sigma, activeNode, focusedNode, showKeyConnectors, highlight, zscoreEdgeColoring, thinning, performanceMode]);

  // timeline selection: cumulative playback changes a few hundred items per
  // tick, so only those are re-reduced and re-uploaded (no re-index, no sort).
  // Entering or leaving the filter touches every item → full reprocess.
  const previousSelectionRef = useRef<TimelineSelection | null>(null);
  useEffect(() => {
    const previous = previousSelectionRef.current;
    previousSelectionRef.current = timeSelection;
    reducerStateRef.current = {
      ...reducerStateRef.current,
      activeEdges: timeSelection?.edges ?? null,
      activeNodes: timeSelection?.nodes ?? null,
    };
    if (!previous || !timeSelection) {
      refreshAll(sigma);
      return;
    }
    const changedNodes = symmetricDifference(previous.nodes, timeSelection.nodes);
    const changedEdges = symmetricDifference(previous.edges, timeSelection.edges);
    if (changedNodes.length === 0 && changedEdges.length === 0) return;
    try {
      sigma.refresh({ partialGraph: { nodes: changedNodes, edges: changedEdges }, skipIndexation: true, schedule: true });
    } catch {
      // an item sigma has not indexed yet (fresh instance) — fall back to the full path
      refreshAll(sigma);
    }
  }, [sigma, timeSelection]);

  // Tooltip is hover-immediate (independent of the dwell-delayed dim);
  // suppress it when the node is already open in the side panel. Kept apart
  // from the reducer effect above on purpose: that one re-runs on every
  // timeline playback tick, and rebuilding the tooltip state there scheduled
  // a React update on every tick — after 50 consecutive ticks React logs
  // "Maximum update depth exceeded" (its passive-effect heuristic), blamed on
  // whichever update came next. The position is refreshed by afterRender.
  useEffect(() => {
    activeNodeRef.current = hoveredNode;
    if (!hoveredNode || hoveredNode === focusedNode) {
      setTooltip(null);
      return;
    }

    const graph = sigma.getGraph();
    const attrs = graph.getNodeAttributes(hoveredNode);

    const mentionCounts: Record<string, number> = {};
    graph.forEachEdge(hoveredNode, (_edge, edgeAttrs) => {
      for (const [type, count] of Object.entries(edgeAttrs.mentionTypes)) {
        mentionCounts[type] = (mentionCounts[type] ?? 0) + count;
      }
    });

    // from the stored coordinates, not getNodeDisplayData(): the setSettings
    // call just above left that cache holding raw coordinates until the next
    // frame (see the camera effect) — afterRender then keeps it in sync
    const pos = sigma.graphToViewport({ x: attrs.x, y: attrs.y });

    setTooltip({
      x: pos.x,
      y: pos.y,
      nodeId: hoveredNode,
      label: attrs.label,
      inEvent: attrs.inEvent,
      community: attrs.community,
      betweenness: attrs.betweenness,
      pagerank: attrs.pagerank,
      hubScore: attrs.hubScore,
      authorityScore: attrs.authorityScore,
      actorRole: attrs.actorRole,
      platform: attrs.platform,
      sourceUrl: attrs.sourceUrl,
      connections: actorConnectionStats(graph, hoveredNode),
      mentionCounts,
      isKeyConnector: attrs.isKeyConnector,
      isHighRisk: attrs.isHighRisk,
      isStateAffiliated: attrs.isStateAffiliated,
      isHighInteraction: attrs.isHighInteraction,
      reciprocity: attrs.reciprocity,
      clusteringCoeff: attrs.clusteringCoeff,
      platformDiversity: attrs.platformDiversity,
      bridgeScore: attrs.bridgeScore,
      weightGini: attrs.weightGini,
      avgZScore: attrs.avgZScore,
      weightedAnomalyScore: attrs.weightedAnomalyScore,
    });
  }, [hoveredNode, focusedNode, sigma]);

  if (!tooltip) return null;

  // hover tooltip is deliberately minimal — name + platform; everything else
  // lives in the Information panel that opens on click
  const TooltipPlatformIcon =
    PLATFORM_ICONS[tooltip.platform] ?? FALLBACK_PLATFORM_ICON;
  const tooltipPlatformLabel =
    platformNamesMapping[tooltip.platform] ?? tooltip.platform;

  return (
    <div className={styles.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
      <div className={styles.tooltipHeader}>
        {tooltip.platform && (
          <span
            className={styles.nodePanelPlatform}
            role="img"
            aria-label={tooltipPlatformLabel}
          >
            <TooltipPlatformIcon aria-hidden="true" />
          </span>
        )}
        <p className={styles.tooltipTitle}>{tooltip.label}</p>
      </div>
    </div>
  );
};
